/**
 * One account.
 *
 * Two things here need a steady hand and say so:
 *
 *   - **Resetting a second factor** is the "I lost my phone" path for somebody
 *     who cannot sign in to fix it themselves. Confirm who you are talking to
 *     before using it.
 *   - **Deleting** is for a tooling-created address that later changed shape,
 *     not for a person who left. Disable them instead, so the audit log keeps
 *     pointing at somebody.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Spinner from '../../components/Spinner';
import {
  Button,
  Chip,
  ConfirmButton,
  Empty,
  Field,
  Input,
  Notice,
  Select,
} from '../../components/ui';
import { api, describeError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import ConsoleLayout from './ConsoleLayout';

interface Membership {
  organizationId: string;
  slug: string;
  name: string;
  role: string;
  acceptedAt: string | null;
}

interface PersonDetail {
  id: string;
  email: string;
  displayName: string | null;
  realm: string;
  keycloakSubject: string | null;
  isPlatformAdmin: boolean;
  disabledAt: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  mfaEnforcedAt: string | null;
  memberships: Membership[];
  sessionCount: number;
}

const ROLES = ['owner', 'admin', 'recruiter', 'hiring_manager', 'interviewer', 'sourcer', 'reviewer'];

export default function Person() {
  const { accountId = '' } = useParams();
  const { identity } = useSession();
  const [person, setPerson] = useState<PersonDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ person: PersonDetail }>(`/api/console/people/${accountId}`);
      setPerson(result.person);
      setError(null);
    } catch (failure) {
      setError(describeError(failure));
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (work: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await work();
      if (success) setMessage(success);
      await load();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  };

  if (error && !person) {
    return (
      <ConsoleLayout back={{ to: '/console/people', label: 'People' }}>
        <Notice tone="danger">{error}</Notice>
      </ConsoleLayout>
    );
  }
  if (!person) {
    return (
      <ConsoleLayout back={{ to: '/console/people', label: 'People' }}>
        <Spinner label="Loading the account" />
      </ConsoleLayout>
    );
  }

  const isMe = person.id === identity?.user?.accountId;

  return (
    <ConsoleLayout back={{ to: '/console/people', label: 'People' }}>
      <header className="mb-8">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[clamp(1.5rem,1.2rem+1vw,2rem)]">
            {person.displayName ?? person.email}
          </h1>
          {person.isPlatformAdmin && <Chip tone="accent">Staff</Chip>}
          {person.disabledAt && <Chip tone="danger">Disabled</Chip>}
          {isMe && <Chip tone="muted">You</Chip>}
        </div>
        <p className="mt-2 font-code text-[12px] text-bone/35">{person.email}</p>
      </header>

      {error && (
        <div className="mb-5">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}
      {message && (
        <div className="mb-5">
          <Notice tone="good">{message}</Notice>
        </div>
      )}

      <div className="space-y-8">
        <section className="panel p-6">
          <h2 className="mb-4 text-[15.5px] font-medium">Account</h2>
          <dl className="grid gap-3 text-[13px] sm:grid-cols-2">
            <Row label="Created" value={formatDate(person.createdAt)} />
            <Row label="Last seen" value={person.lastSeenAt ? formatDate(person.lastSeenAt) : 'never'} />
            <Row label="Active sessions" value={String(person.sessionCount)} />
            <Row
              label="Identity"
              value={person.keycloakSubject ? 'linked' : 'not yet signed in'}
            />
            <Row
              label="Second factor"
              value={person.mfaEnforcedAt ? `required since ${formatDate(person.mfaEnforcedAt)}` : 'optional'}
            />
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              busy={busy}
              onClick={() =>
                act(
                  () =>
                    api.post(`/api/console/people/${person.id}/disabled`, {
                      value: !person.disabledAt,
                    }),
                  person.disabledAt ? 'Enabled.' : 'Disabled, and every session ended.',
                )
              }
              disabled={isMe}
            >
              {person.disabledAt ? 'Enable' : 'Disable'}
            </Button>

            <Button
              busy={busy}
              disabled={isMe && person.isPlatformAdmin}
              title={
                isMe && person.isPlatformAdmin
                  ? 'You cannot revoke your own staff access — the console is the only way back.'
                  : undefined
              }
              onClick={() =>
                act(
                  () =>
                    api.post(`/api/console/people/${person.id}/staff`, {
                      value: !person.isPlatformAdmin,
                    }),
                  person.isPlatformAdmin ? 'Staff access revoked.' : 'Staff access granted.',
                )
              }
            >
              {person.isPlatformAdmin ? 'Revoke staff' : 'Make staff'}
            </Button>

            <ConfirmButton
              variant="secondary"
              confirmLabel="Reset it?"
              busy={busy}
              onConfirm={() =>
                void act(
                  () => api.post(`/api/console/people/${person.id}/reset-mfa`),
                  'Second factor removed. They will be asked to enrol again.',
                )
              }
            >
              Reset second factor
            </ConfirmButton>
          </div>

          <p className="mt-3 max-w-prose text-[12px] leading-relaxed text-bone/35">
            Resetting a second factor removes it and re-imposes the requirement. Confirm who you
            are talking to first — this is the path somebody who has lost their phone asks for, and
            it is also the path somebody pretending to be them would ask for.
          </p>
        </section>

        <MembershipsPanel person={person} onChange={load} />

        <section className="panel border-danger/25 p-6">
          <h2 className="mb-1 text-[15.5px] font-medium text-danger">Delete this account</h2>
          <p className="mb-4 max-w-prose text-[13px] leading-relaxed text-bone/45">
            For an address created by tooling that later changed shape. A person who has left
            should be <strong>disabled</strong> instead, so the audit log keeps pointing at
            somebody. Deleting refuses while they are the last owner of any workspace.
          </p>
          <DeletePerson person={person} disabled={isMe} />
        </section>
      </div>
    </ConsoleLayout>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-veil/[0.05] pb-2">
      <dt className="text-bone/45">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

function MembershipsPanel({
  person,
  onChange,
}: {
  person: PersonDetail;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slug, setSlug] = useState('');
  const [role, setRole] = useState('reviewer');

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChange();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel p-6">
      <h2 className="mb-4 text-[15.5px] font-medium">Workspaces</h2>

      {error && (
        <div className="mb-4">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      {person.memberships.length === 0 ? (
        <Empty title="Not a member of anything." />
      ) : (
        <ul className="divide-y divide-veil/[0.06]">
          {person.memberships.map((membership) => (
            <li key={membership.organizationId} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px]">{membership.name}</p>
                <p className="truncate font-code text-[11.5px] text-bone/35">{membership.slug}</p>
              </div>
              <Select
                value={membership.role}
                onChange={(event) =>
                  act(() =>
                    api.post(`/api/console/people/${person.id}/membership`, {
                      slug: membership.slug,
                      role: event.target.value,
                    }),
                  )
                }
                disabled={busy}
                className="w-auto py-1.5 text-[13px]"
              >
                {ROLES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
              <Button
                variant="quiet"
                busy={busy}
                onClick={() =>
                  act(() =>
                    api.post(`/api/console/people/${person.id}/membership`, {
                      slug: membership.slug,
                      role: null,
                    }),
                  )
                }
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-veil/[0.06] pt-5">
        <div className="min-w-[14rem] flex-1">
          <Field label="Add to a workspace" hint="By its address.">
            <Input
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="acme"
              className="font-code"
            />
          </Field>
        </div>
        <Select
          value={role}
          onChange={(event) => setRole(event.target.value)}
          className="w-auto min-w-[10rem]"
          aria-label="Role"
        >
          {ROLES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <Button
          busy={busy}
          disabled={!slug.trim()}
          onClick={() =>
            act(async () => {
              await api.post(`/api/console/people/${person.id}/membership`, { slug, role });
              setSlug('');
            })
          }
        >
          Add
        </Button>
      </div>
    </section>
  );
}

function DeletePerson({ person, disabled }: { person: PersonDetail; disabled: boolean }) {
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (done) {
    return <Notice tone="good">Deleted. The audit record of it outlives the account.</Notice>;
  }

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.post(`/api/console/people/${person.id}/delete`, { confirmation });
          setDone(true);
        } catch (failure) {
          setError(describeError(failure));
        } finally {
          setBusy(false);
        }
      }}
      className="flex flex-wrap items-end gap-3"
    >
      <div className="min-w-[18rem] flex-1">
        <Field label={`Type ${person.email} to confirm`} error={error}>
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            className="font-code"
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
          />
        </Field>
      </div>
      <Button
        type="submit"
        variant="danger"
        busy={busy}
        disabled={disabled || confirmation.trim().toLowerCase() !== person.email.toLowerCase()}
      >
        Delete
      </Button>
    </form>
  );
}
